import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { debounce } from 'lodash';
import {
  Autocomplete,
  AutocompleteRenderInputParams,
  Box,
  BoxProps,
  IconProps,
  InputAdornment,
  Paper,
  TextField,
  TextFieldProps,
  Typography,
  TypographyProps,
} from '@mui/material';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import SearchIcon from '@mui/icons-material/Search';
import { useFlag } from '@unleash/proxy-client-react';
import { useDispatch } from 'react-redux';
import {
  ISearchOption,
  ISubTopicForSearch,
  ISubjectForSearch,
  ITopicForSearch,
  getCurriculumState,
  getSearchOptionsForHomePage,
} from '../../../store/CurriculumSlice';
import { useAppSelector } from '../../../hooks';
import { getUrlConvertedString, isMobileScreen } from '../../../helper';
import { TP_COLORS } from '../../../constants';

const curriculumTypoProps: TypographyProps = {
  variant: 'h6',
  style: {
    fontWeight: '600',
    padding: '10px 0px',
    paddingLeft: '10px',
  },
};

const subjectTypoProps: TypographyProps = {
  variant: 'subtitle2',
  style: {
    fontWeight: '600',
    padding: '15px 0px',
    paddingLeft: '30px',
  },
};

const topicTypoProps: TypographyProps = {
  variant: 'subtitle2',
  style: {
    color: '#333333',
    fontWeight: '500',
    padding: '10px 0px',
    paddingLeft: '30px',
  },
};

const subTopicTypoProps: TypographyProps = {
  variant: 'subtitle2',
  style: {
    color: '#808080',
    padding: '10px 0px',
    paddingLeft: '50px',
  },
};

const curriculumBoxStyles: BoxProps['style'] = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  cursor: 'pointer',
  width: '90%',
};

const subjectBoxStyles: BoxProps['style'] = {
  ...curriculumBoxStyles,
  border: '1px solid #D0D0D0',
};

const topicBoxStyles: BoxProps['style'] = {
  ...curriculumBoxStyles,
  backgroundColor: '#f6f6f6',
  border: '1px solid #D0D0D052',
};

const optionBoxStyles: BoxProps['style'] = {
  display: 'flex',
  justifyContent: 'center',
};

const iconProps: IconProps['sx'] = {
  fontSize: 'large',
  style: { paddingRight: isMobileScreen() ? '20px' : '50px', color: '#d0d0d0' },
};

const textFieldStyles: TextFieldProps['sx'] = {
  margin: '20px 0px',
  '& .MuiOutlinedInput-root': {
    paddingLeft: '20px !important',
    padding: '5px',
    '&.Mui-focused fieldset': {
      borderColor: '#3B49604E',
    },
  },
};

const customPaperStyles = {
  boxShadow: 3,
  borderRadius: '20px',
  marginTop: 1,
  marginBottom: 1,
};

const SearchBar: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Store
  const curriculumState = useAppSelector(getCurriculumState);

  // State
  const [filteredOptions, setFilteredOptions] = useState<ISearchOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState<string>('');

  const { searchOptions } = curriculumState;

  const defaultOrderEnabled = useFlag(
    'learning_platform_subtopic_default_order_enabled'
  );

  useEffect(() => {
    if (searchOptions.length === 0) {
      dispatch(getSearchOptionsForHomePage());
    }
  }, []);

  useEffect(() => {
    const value = searchKeyword;
    const searchResult: ISearchOption[] = [];

    if (searchOptions.length) {
      setLoading(true);
      searchOptions.forEach((curriculumItem) => {
        const newSubjects: ISubjectForSearch[] = [];
        curriculumItem.subjects?.forEach((subjectItem) => {
          if (subjectItem.topics?.length) {
            let newTopics: ITopicForSearch[] = [];
            subjectItem.topics.forEach((topicItem) => {
              const newTopic: ITopicForSearch = {
                ...topicItem,
                subTopicId: topicItem.subTopics[0]?._id || undefined,
                subTopicName: topicItem.subTopics[0]?.name || undefined,
                subTopics: [],
              };
              const filteredSubTopics = topicItem.subTopics?.filter(
                (subTopic) =>
                  subTopic.name.toLowerCase().includes(value.toLowerCase())
              );
              if (topicItem.name.toLowerCase().includes(value.toLowerCase())) {
                if (filteredSubTopics.length) {
                  newTopic.subTopics = filteredSubTopics;
                }
                newTopics.push(newTopic);
              } else if (filteredSubTopics.length) {
                newTopics.push({
                  ...newTopic,
                  subTopics: filteredSubTopics,
                });
              }
            });

            if (
              curriculumItem.curriculum
                .toLowerCase()
                .includes(value.toLowerCase()) ||
              subjectItem.subject.toLowerCase().includes(value.toLowerCase())
            ) {
              newTopics = subjectItem.topics;
            }
            if (newTopics.length) {
              newSubjects.push({
                ...subjectItem,
                curriculum: curriculumItem.curriculum,
                topics: newTopics,
                subTopicId:
                  subjectItem.topics[0].subTopics.length > 0
                    ? subjectItem.topics[0].subTopics[0]?._id
                    : undefined,
                subTopicName:
                  subjectItem.topics[0].subTopics.length > 0
                    ? subjectItem.topics[0].subTopics[0]?.name
                    : undefined,
              });
            }
          }
        });
        if (newSubjects.length) {
          searchResult.push({
            curriculum: curriculumItem.curriculum,
            subjects: newSubjects,
          });
        }
      });
      setLoading(false);
    }

    if (searchResult.length) {
      setFilteredOptions(searchResult);
    } else {
      setFilteredOptions([]);
    }
    if (value === '') setFilteredOptions([]);
  }, [searchKeyword]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchKeyword(event.target.value);
  };

  const debouncedChangeHandler = useCallback(
    debounce(handleInputChange, 300),
    []
  );

  const handleSubjectNavigate = (subjectData: ISubjectForSearch) => {
    const firstTopic = subjectData.topics[0];
    const urlConvertedCurriculum = getUrlConvertedString(
      subjectData.curriculum || ''
    );
    const urlConvertedSubject = getUrlConvertedString(subjectData.subject);
    const urlConvertedSubjectLevel = getUrlConvertedString(
      firstTopic.subjectLevel
    );
    const urlConvertedSubjectCode = getUrlConvertedString(
      subjectData.subjectCode
    );

    navigate(
      `${process.env.PUBLIC_URL}/dashboard/${urlConvertedCurriculum}/${urlConvertedSubject}/${urlConvertedSubjectLevel}/${urlConvertedSubjectCode}`
    );
  };

  const handleTopicNavigate = (topicData: ITopicForSearch) => {
    const urlConvertedCurriculum = getUrlConvertedString(topicData.curriculum);
    const urlConvertedSubject = getUrlConvertedString(topicData.subject);
    const urlConvertedSubjectLevel = getUrlConvertedString(
      topicData.subjectLevel
    );
    const urlConvertedSubjectCode = getUrlConvertedString(
      topicData.subjectCode
    );
    const urlConvertedTopic = getUrlConvertedString(topicData.name);

    navigate(
      `${process.env.PUBLIC_URL}/dashboard/${urlConvertedCurriculum}/${urlConvertedSubject}/${urlConvertedSubjectLevel}/${urlConvertedSubjectCode}/${urlConvertedTopic}/${topicData._id}`
    );
  };

  const handleSubTopicNavigate = (
    topicData: ITopicForSearch,
    subTopicData: ISubTopicForSearch
  ) => {
    const urlConvertedCurriculum = getUrlConvertedString(topicData.curriculum);
    const urlConvertedSubject = getUrlConvertedString(topicData.subject);
    const urlConvertedTopic = getUrlConvertedString(topicData.name);
    const urlConvertedSubTopic = getUrlConvertedString(subTopicData.name);

    navigate(
      `${process.env.PUBLIC_URL}/dashboard/${urlConvertedCurriculum}/${urlConvertedSubject}/${urlConvertedTopic}/${urlConvertedSubTopic}/${subTopicData._id}`
    );
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      e.stopPropagation();
    }
  };

  const onFocus = () => {
    const parentElement = document.getElementById('top-of-search');
    if (parentElement) {
      parentElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const renderInput = (params: AutocompleteRenderInputParams) => (
    <TextField
      {...params}
      placeholder="What are you looking for?"
      onChange={debouncedChangeHandler}
      onKeyDown={onKeyDown}
      onFocus={onFocus}
      sx={textFieldStyles}
      InputProps={{
        ...params.InputProps,
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon />
          </InputAdornment>
        ),
        sx: {
          borderRadius: '37px',
          backgroundColor: TP_COLORS.white,
          height: '60px',
        },
      }}
    />
  );

  const renderOption = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: ISearchOption
  ) => {
    const optionList: BoxProps[] = [];

    optionList.push(
      <Box
        style={curriculumBoxStyles}
        onClick={() => {
          navigate(
            `${process.env.PUBLIC_URL}/${getUrlConvertedString(
              option.curriculum
            )}`
          );
          window.location.reload();
        }}
      >
        <Typography {...curriculumTypoProps}>{option.curriculum}</Typography>
        <ArrowRightIcon {...iconProps} />
      </Box>
    );

    option?.subjects?.forEach((subjectElement, subjectIndex) => {
      optionList.push(
        <Box
          style={{
            ...subjectBoxStyles,
            ...(subjectIndex === 0
              ? { borderRadius: '10px 10px 0px 0px' }
              : {}),
          }}
          onClick={() => handleSubjectNavigate(subjectElement)}
        >
          <Typography {...subjectTypoProps}>
            {subjectElement.subject}
            {subjectElement.subjectLevel
              ? ` (${subjectElement.subjectLevel})`
              : ''}
          </Typography>
          <ArrowRightIcon {...iconProps} />
        </Box>
      );

      subjectElement?.topics?.forEach((topicElement) => {
        optionList.push(
          <Box
            style={topicBoxStyles}
            onClick={() => handleTopicNavigate(topicElement)}
          >
            <Typography {...topicTypoProps}>
              {`${
                defaultOrderEnabled
                  ? topicElement.index || ''
                  : topicElement.sortingOrder || 0
              }. `}
              {topicElement.name}
            </Typography>
            <ArrowRightIcon {...iconProps} />
          </Box>
        );

        topicElement?.subTopics?.forEach((subTopicElement) => {
          optionList.push(
            <Box
              style={topicBoxStyles}
              onClick={() =>
                handleSubTopicNavigate(topicElement, subTopicElement)
              }
            >
              <Typography {...subTopicTypoProps}>
                {defaultOrderEnabled
                  ? `${topicElement.index || ''}.${
                      subTopicElement.index || ''
                    } `
                  : `${topicElement.sortingOrder || 0}.${
                      subTopicElement.sortingOrder || 0
                    } `}
                {subTopicElement.name}
              </Typography>
              <ArrowRightIcon {...iconProps} />
            </Box>
          );
        });
      });
    });
    return (
      <li
        key={`${option?.curriculum}-${props?.id}`}
        style={{ margin: '10px 0px' }}
      >
        {optionList.map((element, index) => (
          <Box style={optionBoxStyles} key={index}>
            {element}
          </Box>
        ))}
      </li>
    );
  };

  const CustomPaper = (props) => {
    return <Paper {...props} sx={customPaperStyles} />;
  };

  return (
    <Autocomplete
      freeSolo
      disableClearable
      id="search-bar"
      loading={loading}
      options={filteredOptions}
      filterOptions={(options) => options}
      getOptionLabel={(option) => (option as ISearchOption).curriculum}
      PaperComponent={CustomPaper}
      renderInput={renderInput}
      renderOption={renderOption}
      ListboxProps={{
        style: {
          maxHeight: isMobileScreen() ? '50vh' : '60vh',
        },
      }}
    />
  );
};

export default SearchBar;
